import { useEffect, useState } from 'react';
import {
  Alert,
  Breadcrumb,
  Button,
  Input,
  ImageUpload,
  PageHeader,
  SelectMenu,
  SettingsPanel,
  SettingsRow,
} from '@jordan/ui';
import { AppChrome } from '../components/AppChrome';
import { useConfig } from '../config/ConfigProvider';
import { apiDelete, apiGet, apiPatch, apiPut, logoUrl } from '../lib/api';
import { TIMEZONES } from '../openslot/helpers';

export function GeneralSettingsPage() {
  const cfg = useConfig();
  const [appName, setAppName] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [copyright, setCopyright] = useState('');
  const [pendingLogo, setPendingLogo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ appName: string; timezone: string; copyright: string }>('/api/config')
      .then((c) => {
        setAppName(c.appName);
        setTimezone(c.timezone);
        setCopyright(c.copyright);
      })
      .catch(() => setError('Failed to load settings.'));
  }, []);

  const run = async (fn: () => Promise<void>, ok: string) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await fn();
      await cfg.refresh();
      setMessage(ok);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const saveName = () => run(() => apiPatch('/api/config', { appName }), 'Application name saved.');
  const saveCopyright = () => run(() => apiPatch('/api/config', { copyright }), 'Copyright saved.');
  const saveTimezone = (tz: string) => {
    setTimezone(tz);
    void run(() => apiPatch('/api/config', { timezone: tz }), 'Default timezone saved.');
  };
  const saveLogo = () =>
    run(async () => {
      await apiPut('/api/config/logo', { dataUrl: pendingLogo });
      setPendingLogo(null);
    }, 'Logo updated.');
  const removeLogo = () =>
    run(async () => {
      await apiDelete('/api/config/logo');
      setPendingLogo(null);
    }, 'Logo removed.');

  const currentLogo = pendingLogo ?? (cfg.hasLogo ? logoUrl(cfg.logoUpdatedAt) : null);

  return (
    <AppChrome>
      <PageHeader
        title={
          <>
            Configuration - <span className="fw-normal">General</span>
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[
              { icon: 'ph-house', href: '/' },
              { label: 'Configuration' },
              { label: 'General' },
            ]}
          />
        }
      />

      <div className="content">
        {message && (
          <Alert variant="success" icon="ph-check-circle" dismissible>
            {message}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {error}
          </Alert>
        )}

        <SettingsPanel
          title="General"
          description="Branding and core settings for the application."
        >
          <SettingsRow
            title="Application name"
            description="Shown in the header when no logo is set, and used as the page title."
          >
            <div className="d-flex align-items-start gap-2" style={{ maxWidth: 480 }}>
              <div className="flex-grow-1">
                <Input value={appName} onChange={(e) => setAppName(e.target.value)} />
              </div>
              <Button variant="primary" disabled={busy || !appName.trim()} onClick={saveName}>
                Save
              </Button>
            </div>
          </SettingsRow>

          <SettingsRow
            title="Default timezone"
            description="The app-wide default timezone — used as the default for new availability schedules."
          >
            <div style={{ maxWidth: 360 }}>
              <SelectMenu
                icon="ph-globe"
                value={timezone}
                onChange={saveTimezone}
                options={TIMEZONES.map((t) => ({ value: t, label: t }))}
              />
            </div>
          </SettingsRow>

          <SettingsRow
            title="Copyright"
            description="Shown in the footer. Use {year} for the current year, e.g. © {year} Acme Inc."
          >
            <div className="d-flex align-items-start gap-2" style={{ maxWidth: 480 }}>
              <div className="flex-grow-1">
                <Input value={copyright} onChange={(e) => setCopyright(e.target.value)} />
              </div>
              <Button variant="primary" disabled={busy} onClick={saveCopyright}>
                Save
              </Button>
            </div>
          </SettingsRow>

          <SettingsRow
            title="Logo"
            description="Displayed in the application header. Recommended height 32px; PNG, SVG, JPG or WebP up to 1.5 MB."
          >
            <ImageUpload
              previewSrc={currentLogo}
              onSelect={(_file, dataUrl) => {
                setPendingLogo(dataUrl);
                setMessage(null);
              }}
              onRemove={cfg.hasLogo || pendingLogo ? removeLogo : undefined}
              hint={pendingLogo ? 'Preview — click “Save logo” to apply.' : undefined}
            />
            {pendingLogo && (
              <div className="mt-3">
                <Button variant="primary" disabled={busy} onClick={saveLogo}>
                  Save logo
                </Button>
                <Button variant="light" className="ms-2" disabled={busy} onClick={() => setPendingLogo(null)}>
                  Cancel
                </Button>
              </div>
            )}
          </SettingsRow>
        </SettingsPanel>
      </div>
    </AppChrome>
  );
}
